import { offersLiveEditor } from '@ordergroove/offers-live-editor';
import { setStore } from './core/connect';
import * as adapters from './core/adapters';
import * as actions from './core/actions';
import { ConnectedWhen } from './components/When';
import { ConnectedOptinButton } from './components/OptinButton';
import { ConnectedOptoutButton } from './components/OptoutButton';
import { ConnectedOptinSelect } from './components/OptinSelect';
import { ConnectedUpsellButton } from './components/UpsellButton';
import { ConnectedUpsellModal } from './components/UpsellModal';
import { ConnectedOptinToggle } from './components/OptinToggle';
import { ConnectedOptinStatus } from './components/OptinStatus';
import { ConnectedText } from './components/Text';
import { ConnectedIncentiveText } from './components/IncentiveText';
import { ConnectedBenefitMessages } from './components/BenefitMessages';
import { ConnectedSelectFrequency } from './components/SelectFrequency';
import { ConnectedNextUpcomingOrder } from './components/NextUpcomingOrder';
import { ConnectedOffer } from './components/Offer';
import { Modal } from './components/Modal';
import { Select } from './components/Select';
import { Tooltip } from './components/Tooltip';
import { ConnectedFrequencyStatus } from './components/FrequencyStatus';
import { ConnectedPrepaidToggle } from './components/PrepaidToggle';
import { ConnectedPrepaidData } from './components/PrepaidData';
import { ConnectedPrepaidButton } from './components/PrepaidButton';
import { ConnectedPrepaidSelect } from './components/PrepaidSelect';
import { ConnectedSubscriptionButton } from './components/SubscriptionButton';
import * as testMode from './test-mode';
import { RECEIVE_PRODUCT_PLANS } from './core/constants';
import ConnectedPrice from './components/Price';
import platform from './platform';

export default function makeApi(store) {
  testMode.enable();

  setStore(store);

  try {
    customElements.define('og-when', ConnectedWhen);
    customElements.define('og-text', ConnectedText);
    customElements.define('og-incentive-text', ConnectedIncentiveText);
    customElements.define('og-benefit-messages', ConnectedBenefitMessages);
    customElements.define('og-offer', ConnectedOffer);
    customElements.define('og-select-frequency', ConnectedSelectFrequency);
    customElements.define('og-optout-button', ConnectedOptoutButton);
    customElements.define('og-optin-toggle', ConnectedOptinToggle);
    customElements.define('og-optin-status', ConnectedOptinStatus);
    customElements.define('og-optin-button', ConnectedOptinButton);
    customElements.define('og-optin-select', ConnectedOptinSelect);
    customElements.define('og-upsell-button', ConnectedUpsellButton);
    customElements.define('og-frequency-status', ConnectedFrequencyStatus);
    customElements.define('og-modal', Modal);
    customElements.define('og-select', Select);
    customElements.define('og-tooltip', Tooltip);
    customElements.define('og-upsell-modal', ConnectedUpsellModal);
    customElements.define('og-next-upcoming-order', ConnectedNextUpcomingOrder);
    customElements.define('og-price', ConnectedPrice);
    customElements.define('og-prepaid-toggle', ConnectedPrepaidToggle);
    customElements.define('og-prepaid-data', ConnectedPrepaidData);
    customElements.define('og-prepaid-button', ConnectedPrepaidButton);
    customElements.define('og-prepaid-select', ConnectedPrepaidSelect);
    customElements.define('og-subscription-button', ConnectedSubscriptionButton);
  } catch (err) {
    console.info('OG WebComponents already registered, skipping.');
  }

  // // use this syntax to allow es6 module be called as default function og.offers(...)
  // module.exports = offers.initialize;
  let isReady = false;
  const offers = {
    store,
    isReady: () => isReady,
    setEnvironment(e) {
      store.dispatch(actions.setEnvironment(e));
      return this;
    },
    setMerchantId(m) {
      store.dispatch(actions.setMerchantId(m));
      return this;
    },
    setAuthUrl(authUrl) {
      store.dispatch(actions.setAuthUrl(authUrl));
      return this;
    },
    receiveMerchantSettings(settings) {
      store.dispatch(actions.receiveMerchantSettings(settings));
      return this;
    },
    getProductsForPurchasePost(productIds = []) {
      return adapters.getProductsForPurchasePost(store.getState(), productIds);
    },
    getOptins(productIds = []) {
      return adapters.getProductsForPurchasePost(store.getState(), productIds);
    },
    clear() {
      store.dispatch(actions.checkout());
    },
    addOptinChangedCallback(fn) {
      if (typeof fn === 'function') document.addEventListener('optin-changed', e => fn(e.detail));
    },
    disableOptinChangedCallbacks() {
      document.addEventListener('optin-changed', e => e.stopPropagation(), true);
    },

    register() {
      /* noop */
    },
    previewMode(set) {
      window.og = window.og || {};
      if (set === false) {
        delete window.og;
      } else {
        window.og.previewMode = true;
        console.log('OG Offers preview mode enabled');
      }
      return this;
    },
    config(configuration) {
      store.dispatch(actions.setConfig(configuration));
      return this;
    },
    setLocale(locale) {
      store.dispatch(actions.setLocale(locale));
      return this;
    },
    /**
     * Set benefit messages keyed by incentive public id. Each value is a
     * locale map (IETF tag → message text) for the incentive; the consumer
     * picks the appropriate locale at render time. Replaces the entire map.
     * @param {{ [incentivePublicId: string]: { [locale: string]: string } }} messages
     */
    setBenefitMessages(messages) {
      store.dispatch(actions.setBenefitMessages(messages));
      return this;
    },
    addTemplate(tagName, content, configOption) {
      store.dispatch(actions.addTemplate(tagName, content, configOption));
      return this;
    },
    /**
     * templates object where keys are selectors and values are content
     */
    setTemplates(templates) {
      store.dispatch(actions.setTemplates(templates));
      return this;
    },

    setPublicPath(_publicPath) {
      return this;
    },

    resolveSettings(merchantId, env, settings, storeInstance = store) {
      // window.og_settings does not affect Shopify selling plan offers
      if (platform.shopify_selling_plans) return;

      if (merchantId && env && settings) {
        let products = [];
        if (settings.product) {
          products.push(settings.product);
        } else if (settings.cart && Array.isArray(settings.cart.products)) {
          products = products.concat(settings.cart.products);
        }

        const state = storeInstance.getState();
        const { sessionId } = state;
        if (sessionId) {
          products.forEach(product => storeInstance.dispatch(actions.requestOffer(product)));
        }

        if (settings.product_discounts && typeof settings.product_discounts === 'object') {
          storeInstance.dispatch({ type: RECEIVE_PRODUCT_PLANS, payload: settings.product_discounts });
        }
      }
    },

    /**
     * Initialize OG object
     * @param {*} merchantId
     * @param {*} env
     * @param {*} authUrl
     * @param {import('./core/types/api').MerchantSettings} merchantSettings
     */
    initialize(merchantId, env, authUrl, merchantSettings = {}) {
      // settings resolves once, before anything.
      if (isReady) {
        console.warn('og.offers has been initialized already. Skipping.');
      }

      const state = store.getState();
      // dont re-trigger actions if value is the same. allowing set new authurl only
      if (merchantId && merchantId !== state.merchantId) offers.setMerchantId(merchantId);
      if (env && env !== state.environment?.name) offers.setEnvironment(env);
      // always set merchant settings
      offers.receiveMerchantSettings(merchantSettings);

      // allow set new authUrl
      if (authUrl) offers.setAuthUrl(authUrl);

      // ensure to dispatch settings actions before any other action
      if (!isReady) {
        offers.resolveSettings(merchantId, env, window.og_settings, store);
      }

      isReady = true;

      return this;
    }
  };

  window.OG = window.OG || {};
  Object.assign(window.OG, offers);
  Object.assign(offers.initialize, offers);

  offersLiveEditor(window.opener, offers);

  return offers;
}
